import { useEffect, useState } from "react";
import { Bell, BellOff, ChevronRight, Lock, LockOpen, MoreHorizontal, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// Lote 96 — "Mais ações" da publicação: reúne o que é raro ou de efeito amplo (segredo de justiça,
// parar de receber o processo, excluir). Abrir o menu NÃO executa nada: cada item pede confirmação
// no próprio app. Desktop = menu suspenso; celular = painel de baixo.
function useMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => { try { return window.matchMedia(q).matches; } catch { return false; } });
  useEffect(() => {
    let mq; try { mq = window.matchMedia(q); } catch { return undefined; }
    const f = () => setM(mq.matches);
    mq.addEventListener?.("change", f);
    return () => mq.removeEventListener?.("change", f);
  }, []);
  return m;
}

export default function PublicacaoMaisAcoes({ item, isBlocked, busy, onSecret, onBlock, onDelete }) {
  const mobile = useMobile();
  const [open, setOpen] = useState(false);
  const id = item.external_id;
  const temProc = Boolean(item.case_number);

  const entries = [];
  if (temProc) {
    entries.push({
      key: "secret", testid: `toggle-secret-processo-${id}`, icon: item.is_secret ? LockOpen : Lock,
      title: item.is_secret ? "Não é segredo de justiça" : "Marcar como segredo de justiça",
      hint: item.is_secret ? "Volta a guardar nomes e texto das próximas publicações" : "Não guarda nomes nem texto e não envia à IA",
      run: onSecret,
    });
    entries.push({
      key: "block", testid: `toggle-block-processo-${id}`, icon: isBlocked ? Bell : BellOff,
      title: isBlocked ? "Voltar a receber publicações deste processo" : "Não receber mais publicações deste processo",
      hint: isBlocked ? "As próximas voltam a entrar na caixa" : "Vale para o Diário e o DataJud · pede confirmação",
      run: onBlock,
    });
  }
  entries.push({
    key: "del", testid: `excluir-${id}`, icon: Trash2, danger: true, title: "Excluir publicação…",
    hint: "Mostra o que acontece antes de excluir", run: onDelete,
  });

  const trigger = (props) => (
    <button
      type="button" data-testid={`mais-acoes-${id}`} disabled={busy} {...props}
      onClick={(e) => { e.stopPropagation(); props.onClick?.(e); }}
      className="inline-flex items-center justify-center gap-2 h-11 sm:h-10 px-3.5 rounded-xl border border-stone-300 bg-stone-100 text-stone-700 text-[13px] font-semibold hover:bg-stone-200 disabled:opacity-60 w-full sm:w-auto"
    >
      <MoreHorizontal className="w-4 h-4" strokeWidth={2} /> Mais ações
    </button>
  );

  if (mobile) {
    return (
      <>
        {trigger({ onClick: () => setOpen(true) })}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl pb-6" data-testid={`mais-acoes-sheet-${id}`} onClick={(e) => e.stopPropagation()}>
            <SheetHeader className="text-left">
              <SheetTitle className="font-display text-xl">Mais ações</SheetTitle>
            </SheetHeader>
            <ul className="mt-2">
              {entries.map((e) => (
                <li key={e.key} className="border-b border-stone-100 last:border-0">
                  <button
                    type="button" data-testid={e.testid} onClick={() => { setOpen(false); e.run(); }}
                    className={`w-full flex items-center gap-3 py-3.5 text-left ${e.danger ? "text-rose-700" : "text-stone-900"}`}
                  >
                    <e.icon className="w-5 h-5 shrink-0" strokeWidth={1.8} />
                    <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold">{e.title}</span><span className={`block text-xs ${e.danger ? "text-rose-500" : "text-stone-500"}`}>{e.hint}</span></span>
                    <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger({})}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 p-1.5" onClick={(e) => e.stopPropagation()}>
        {entries.map((e) => (
          <div key={e.key}>
            {e.danger && <DropdownMenuSeparator />}
            <DropdownMenuItem
              data-testid={e.testid} onSelect={() => e.run()}
              className={`gap-3 items-start py-2.5 cursor-pointer ${e.danger ? "text-rose-700 focus:text-rose-700" : ""}`}
            >
              <e.icon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
              <span className="min-w-0"><span className="block text-[13px] font-semibold">{e.title}</span><span className={`block text-[11.5px] whitespace-normal ${e.danger ? "text-rose-500" : "text-stone-500"}`}>{e.hint}</span></span>
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
